import React, { useEffect, useMemo, useCallback } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import Dialog from '../../../../../../../common/element/Dialog'
import BtnPrimary from '../../../../../../../common/element/Buttons/btnPrimary'
import Select from '../../../../../../../common/element/Select'
import ThreeDotLoader from "../../../../../../../common/element/ButtonLoader/ThreeDotLoader"
import SweetAlertService from "../../../../../../../common/SweetAlert/SweetAlert"
import CommonPhoneInput from '../../../../../../../common/CommonPhoneInput/CommonPhoneInput';

import { useDispatch, useSelector } from 'react-redux'
import { Toollist } from "../../../../../../../redux/SessionMaster/sessionMasterSlice";
import { selectToolListData } from "../../../../../../../redux/SessionMaster/sessionMasterSelector";
import { createInterviewCandidate, clearCreateCandidateState } from "../../../../../../../redux/InterViewCandidate/interViewCandidateSlice";
import { selectCreateCandidateLoading, selectCreateCandidateSuccess, selectCreateCandidateError, selectCreateCandidateData } from "../../../../../../../redux/InterViewCandidate/interViewSelector";
import { useSearchParams, useLocation } from 'react-router-dom'
import { formatToolName } from '../../../../../../../common/Formatters.js/formatters'

// ✅ Import Company Adhoc Actions & Selectors
import { getCompanyAdhocTools, clearCompanyAdhocTools } from "../../../../../../../redux/AdhocCompany/Companyadhocslice";
import { selectCompanyAdhocTools, selectCompanyAdhocToolsLoading } from "../../../../../../../redux/AdhocCompany/Companyadhocselectors";
import { ROLES } from '../../../../../../../config/rolePermissions'
import { selectUserData } from '../../../../../../../redux/auth/authSelector'



// Validation Schema
const validationSchema = Yup.object().shape({
    candidateName: Yup.string()
        .trim()
        .required('Candidate Name is required')
        .min(2, 'Name must be at least 2 characters')
        .max(100, 'Name must not exceed 100 characters'),

    candidateEmail: Yup.string()
        .trim()
        .required('Candidate Email is required')
        .email('Please enter a valid email address'),

    dateOfInterview: Yup.date()
        .required('Date of Interview is required')
        .min(new Date().toISOString().split('T')[0], 'Interview date cannot be in the past')
        .nullable(),

    selectTools: Yup.array()
        .min(1, 'Select at least one tool')
        .required('Select Tools is required'),
});

const AddCandidate = ({
    dialogOpen,
    onDialogClose,
    candidateData,
    isViewMode = false,
    onSuccess,
}) => {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    // ✅ Get User Data and Role
    const userData = useSelector(selectUserData);
    const userRole = userData?.role_name;

    const companyId = searchParams.get("companyId") || userData?.company_id;
    const tab = searchParams.get("tab");


    // ✅ Determine which API to use based on route OR user role
    const isAdhocCompanyRoute = (location.pathname.includes('/adhoc-company/view') &&
        tab === 'interviewCandidates') ||
        userRole === ROLES.ADHOCHR;
    // ✅ Get data from both sources
    const defaultToolData = useSelector(selectToolListData);
    const adhocToolData = useSelector(selectCompanyAdhocTools);
    const adhocToolLoading = useSelector(selectCompanyAdhocToolsLoading);

    const createLoading = useSelector(selectCreateCandidateLoading);
    const createSuccess = useSelector(selectCreateCandidateSuccess);
    const createError = useSelector(selectCreateCandidateError);
    const createData = useSelector(selectCreateCandidateData);

    // ✅ Select the appropriate tool data based on route or role
    const toolData = useMemo(() => {
        if (isAdhocCompanyRoute) {
            return adhocToolData || [];
        }
        return defaultToolData || [];
    }, [isAdhocCompanyRoute, adhocToolData, defaultToolData]);


    // ✅ Transform tool data to options format
    const toolOptions = useMemo(() => {
        return (toolData || []).map((item) => ({
            value: item.id || item.tool_id,
            label: formatToolName(item.tool || item.name || item.tool_name),
        }));
    }, [toolData]);

    const getInitialValues = useCallback(() => {
        if (candidateData) {
            let toolsValue = [];
            if (candidateData.tool_ids && Array.isArray(candidateData.tool_ids)) {
                toolsValue = toolOptions.filter(opt => candidateData.tool_ids.includes(opt.value));
            } else if (candidateData.tools) {
                const match = toolOptions.find(opt => opt.label === candidateData.tools) ||
                    toolOptions.find(opt => opt.value === candidateData.tool_id);
                if (match) toolsValue = [match];
                else toolsValue = [{ value: candidateData.tools?.toLowerCase(), label: candidateData.tools }];
            }

            return {
                candidateName: candidateData.candidateName || candidateData.name || "",
                candidateEmail: candidateData.candidateEmail || candidateData.email || "",
                candidatePhoneNumber: candidateData.candidatePhoneNumber || candidateData.phone_number || "",
                dateOfInterview: candidateData.dateOfInterview ||
                    (candidateData.interview_date ? candidateData.interview_date.split('T')[0] : ""),
                selectTools: toolsValue,
                phoneCountry: null
            };
        }

        return {
            candidateName: "",
            candidateEmail: "",
            candidatePhoneNumber: "",
            dateOfInterview: "",
            selectTools: [],
            phoneCountry: null
        };
    }, [candidateData, toolOptions]);

    // ✅ Fetch appropriate tool list based on route or role
    useEffect(() => {
        if (isAdhocCompanyRoute && companyId) {
            // Fetch Adhoc Company Tools
            dispatch(getCompanyAdhocTools(companyId));
        } else if (isAdhocCompanyRoute && !companyId) {
            // If ADHOC HR but no companyId, log warning or handle accordingly
            dispatch(getCompanyAdhocTools(companyId));
            console.warn("ADHOC HR user but no companyId available");
        } else {
            // Fetch Default Tool List
            dispatch(Toollist());
        }

        // Cleanup on unmount
        return () => {
            if (isAdhocCompanyRoute) {
                dispatch(clearCompanyAdhocTools());
            }
        };
    }, [dispatch, isAdhocCompanyRoute, companyId]);

    useEffect(() => {
        if (createSuccess && createData) {
            const successMessage = createData?.message || createData?.msg || "Candidate added successfully!";
            SweetAlertService.showSuccess({ title: "Success!", message: successMessage });
            if (onSuccess) onSuccess();
            onDialogClose();
            dispatch(clearCreateCandidateState());
        }
    }, [createSuccess, createData, dispatch, onDialogClose, onSuccess]);

    useEffect(() => {
        if (createError) {
            let errorMessage = "Failed to add candidate. Please try again.";
            if (typeof createError === 'string') errorMessage = createError;
            else if (typeof createError === 'object' && createError !== null)
                errorMessage = createError.message || createError.error || createError.msg || errorMessage;
            SweetAlertService.showError({ title: "Error", message: errorMessage });
        }
    }, [createError]);

    useEffect(() => {
        if (!dialogOpen) dispatch(clearCreateCandidateState());
    }, [dialogOpen, dispatch]);

    const isEdit = !!candidateData?.id && !isViewMode;
    const isView = isViewMode;

    const formatInterviewDate = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        date.setUTCHours(10, 0, 0, 0);
        return date.toISOString();
    };

    const handlePhoneNumberChange = (value, country, setFieldValue, setFieldError, setFieldTouched) => {
        setFieldValue('candidatePhoneNumber', value, false);
        setFieldValue('phoneCountry', country, false);
        setFieldTouched('candidatePhoneNumber', true, false);

        if (!value) {
            setFieldError('candidatePhoneNumber', "Candidate Phone Number is required");
            return;
        }

        if (country && country.format) {
            const requiredLength = (country.format.match(/\./g) || []).length;

            if (value.length < requiredLength) {
                setFieldError('candidatePhoneNumber', "Invalid phone number");
            } else {
                setFieldError('candidatePhoneNumber', undefined);
            }
        } else {
            setFieldError('candidatePhoneNumber', undefined);
        }
    };

    const handleSubmit = (values, { setSubmitting, setFieldError }) => {
        if (!values.candidatePhoneNumber) {
            setFieldError('candidatePhoneNumber', "Candidate Phone Number is required");
            setSubmitting(false);
            return;
        }

        const country = values.phoneCountry;
        if (country && country.format) {
            const requiredLength = (country.format.match(/\./g) || []).length;
            if (values.candidatePhoneNumber.length < requiredLength) {
                setFieldError('candidatePhoneNumber', "Invalid phone number");
                setSubmitting(false);
                return;
            }
        }

        dispatch(clearCreateCandidateState());

        const apiPayload = {
            name: values.candidateName.trim(),
            email: values.candidateEmail.trim(),
            phone_number: values.candidatePhoneNumber,
            interview_date: formatInterviewDate(values.dateOfInterview),
            tool_ids: (values.selectTools || []).map(opt => opt.value),
            company_id: companyId
        };

        dispatch(createInterviewCandidate(apiPayload));
        setSubmitting(false);
    };

    const handleDialogClose = () => {
        dispatch(clearCreateCandidateState());
        onDialogClose();
    };

    // ✅ Check if tools are loading
    const isToolsLoading = isAdhocCompanyRoute ? adhocToolLoading : false;

    return (
        <Dialog
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            width={700}
            onRequestClose={handleDialogClose}
            bodyOpenClassName="overflow-hidden"
        >
            <div className="flex border-b mb-5 pb-5 px-6 pt-6 items-center justify-between">
                <div className="flex items-center">
                    <span className="h-8 w-8 border rounded-lg border-gray-200 flex items-center justify-center shadow-sm mr-4">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M13.1649 17.8132C13.1649 17.4545 13.2057 17.1021 13.2856 16.7613C13.1747 16.8429 13.038 16.882 12.8995 16.8633C12.7379 16.8429 12.595 16.7471 12.5089 16.6068L11.7659 15.3729L11.0229 16.6068C10.9368 16.7479 10.7957 16.8429 10.6323 16.8633C10.6093 16.866 10.5844 16.8678 10.564 16.8678C10.4273 16.8678 10.2977 16.8154 10.1983 16.7204L6.93955 13.666C6.71497 13.7068 6.48506 13.7344 6.26224 13.7566C6.03943 13.777 5.80509 13.8018 5.57162 13.8453C4.87834 13.9705 4.24009 14.1995 3.67643 14.52C2.70175 15.0765 2.36089 15.9944 2.12921 17.2967C1.9428 18.3237 1.95877 19.1537 2.17271 19.8373C2.44079 20.6984 3.95695 21.0845 4.76831 21.2967L4.91833 21.3357C5.00265 21.3561 5.0861 21.3765 5.17309 21.397V18.9309C5.17309 18.8013 5.27784 18.6966 5.40743 18.6966C5.53702 18.6966 5.64176 18.8013 5.64176 18.9309V21.5008C7.18722 21.8346 8.93228 21.9988 10.9793 21.9988H12.5656C13.645 21.9988 14.6268 21.9553 15.5402 21.8648C14.0951 21.0534 13.1676 19.5178 13.1676 17.8134L13.1649 17.8132Z" fill="#344054" />
                            <path d="M20.1199 16.1083C20.0879 16.0763 20.0471 16.0586 20.0036 16.0586C19.9583 16.0586 19.9175 16.0763 19.8855 16.1083L17.2969 18.7421C17.2268 18.8149 17.1309 18.8557 17.0306 18.8557C16.9303 18.8557 16.8353 18.8149 16.7643 18.7447L15.7417 17.7062C15.7098 17.6724 15.6689 17.6538 15.6254 17.6538C15.5819 17.6538 15.5393 17.6715 15.5091 17.7035L15.2251 17.992C15.1567 18.0603 15.1567 18.174 15.2251 18.2396L16.9205 19.9644C16.9525 19.9981 16.9889 20.0141 17.0342 20.0141C17.0706 20.0141 17.107 19.9981 17.1363 19.9662L20.402 16.6479C20.4677 16.5796 20.4677 16.466 20.402 16.3976L20.1179 16.1091L20.1199 16.1083Z" fill="#344054" />
                            <path d="M7.4766 13.5297L10.524 16.3837C10.5417 16.4015 10.5648 16.4041 10.5763 16.4015C10.5923 16.3988 10.6154 16.3926 10.6287 16.3651L11.4631 14.9812L9.74281 13.6657C9.47207 13.4562 9.2404 13.2067 9.01758 12.9635L8.86756 12.8019C8.86756 12.8019 8.85602 12.7904 8.85158 12.7886C8.83827 12.7753 8.75571 12.7087 8.66517 12.7815C8.42018 12.975 8.20179 13.1428 7.95857 13.2928C7.80412 13.3887 7.647 13.4633 7.47656 13.529L7.4766 13.5297Z" fill="#344054" />
                            <path d="M17.8143 13.628C17.5276 13.628 17.2462 13.6599 16.9755 13.7141C16.971 13.7168 16.9666 13.7168 16.9621 13.7185C16.7376 13.7638 16.5192 13.8277 16.3195 13.9094C15.3058 14.2955 14.4696 15.082 14.0177 16.0638C13.7612 16.6142 13.6289 17.2 13.6289 17.8134C13.6289 19.6083 14.7678 21.1973 16.4649 21.7768C16.472 21.7768 16.4765 21.7795 16.4827 21.7812C16.9079 21.9268 17.3579 21.9996 17.8142 21.9996C20.123 21.9996 22.0005 20.1222 22.0005 17.8133C22.0005 15.5044 20.1231 13.627 17.8142 13.627L17.8143 13.628ZM20.7365 16.9728L17.4734 20.2883C17.3598 20.4064 17.2054 20.4765 17.0349 20.4792C16.8574 20.4765 16.7012 20.4091 16.5849 20.2857L14.8938 18.5654C14.6462 18.3177 14.6462 17.9111 14.8938 17.6635L15.1735 17.3767C15.2915 17.2542 15.4504 17.1859 15.6235 17.1859C15.7966 17.1859 15.9529 17.2516 16.0736 17.3767L17.0305 18.3496L19.5534 15.7815C19.6741 15.6608 19.833 15.5933 20.0034 15.5933H20.0061C20.1739 15.5933 20.331 15.659 20.4517 15.7815L20.7384 16.07C20.8592 16.1925 20.9222 16.3585 20.9222 16.5201C20.9222 16.6816 20.8609 16.8476 20.7384 16.9719L20.7365 16.9728Z" fill="#344054" />
                            <path d="M11.7835 12.2351C10.9588 12.2351 10.1883 11.8623 9.55892 11.2285C9.54117 11.7079 9.44264 12.1624 9.2793 12.5627L9.36096 12.6532C9.57933 12.892 9.78616 13.1148 10.025 13.2986L11.7701 14.6328L13.5153 13.2986C13.7541 13.1148 13.9609 12.892 14.1793 12.6532L14.2636 12.5627C14.1003 12.1694 14.0044 11.7265 13.984 11.2534C13.3475 11.8854 12.5868 12.2325 11.7861 12.2325L11.7835 12.2351Z" fill="#344054" />
                            <path d="M12.659 5.41985C11.6932 6.45577 10.4141 7.21472 9.02764 7.5698C9.00456 7.5769 8.96639 7.58755 8.92289 7.60175C8.80483 7.64081 8.64592 7.68786 8.48438 7.71804C8.76134 10.0428 10.1523 11.7676 11.784 11.7676C12.6184 11.7676 13.42 11.3131 14.0387 10.4858C14.7293 9.56789 15.1225 8.27454 15.1297 6.92436C14.1612 6.70865 13.2824 6.16983 12.6593 5.41797L12.659 5.41985Z" fill="#344054" />
                            <path d="M16.0421 13.5214C15.8788 13.4601 15.7261 13.3847 15.5788 13.2968C15.3355 13.1468 15.1172 12.979 14.8766 12.7881C14.7834 12.7153 14.7017 12.7793 14.6858 12.7926L14.5198 12.9674C14.2996 13.2107 14.0679 13.461 13.7945 13.6696L12.0742 14.9851L12.9086 16.369C12.9246 16.3965 12.9477 16.4027 12.961 16.4054C12.9743 16.4081 12.9947 16.4054 13.0134 16.3894L13.6152 15.8257C14.1061 14.7916 14.9831 13.9599 16.0422 13.524L16.0421 13.5214Z" fill="#344054" />
                            <path d="M15.3817 6.50328H15.3905C15.4545 6.23254 15.4882 5.95292 15.4882 5.67152C15.4882 3.64671 13.8229 2.00195 11.7795 2.00195C10.7729 2.00195 9.8364 2.40406 9.14303 3.13109C8.37519 3.93977 7.98639 5.0538 8.05207 6.26027V6.34903C8.08136 7.00325 8.13817 7.21452 8.17723 7.2811C8.18433 7.28377 8.19765 7.28377 8.21629 7.28377C8.37518 7.28111 8.62995 7.2021 8.78175 7.15417C8.82968 7.14085 8.87229 7.12665 8.90247 7.11777C10.2935 6.76359 11.5656 5.97711 12.4886 4.90478C12.5366 4.85064 12.6049 4.81868 12.6768 4.82312C12.7469 4.82578 12.8135 4.86218 12.8544 4.91899C13.4384 5.73032 14.3448 6.30732 15.3407 6.49816C15.3433 6.50083 15.3584 6.50083 15.3797 6.5026L15.3817 6.50328Z" fill="#344054" />
                        </svg>
                    </span>
                    <div>
                        <h5 className="mb-0 text-gray-900 text-base font-semibold">
                            {isView ? "View Candidate" : isEdit ? "Edit Candidate" : "Add Candidate"}
                        </h5>
                    </div>
                </div>

                <button
                    onClick={handleDialogClose}
                    className="text-gray-400 hover:text-gray-600"
                    disabled={createLoading}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>

            <Formik
                initialValues={getInitialValues()}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize={true}
            >
                {({ values, setFieldValue, setFieldTouched, setFieldError, errors, touched, isSubmitting }) => (
                    <Form>
                        <div className="px-6 pb-5">
                            {/* Candidate Name */}
                            <div className="flex items-center mb-4">
                                <label className="font-medium text-sm leading-[150%] tracking-normal text-gray-700 w-48 flex-shrink-0">
                                    Candidate Name*
                                </label>
                                <div className="flex-1">
                                    <Field
                                        name="candidateName"
                                        className="h-10 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        placeholder="Enter Candidate name here"
                                        disabled={isViewMode || createLoading}
                                    />
                                    <ErrorMessage name="candidateName">
                                        {msg => <p className="text-xs text-red-600 mt-1">{msg}</p>}
                                    </ErrorMessage>
                                </div>
                            </div>

                            {/* Candidate Email */}
                            <div className="flex items-center mb-4">
                                <label className="font-medium text-sm leading-[150%] tracking-normal text-gray-700 w-48 flex-shrink-0">
                                    Candidate Email*
                                </label>
                                <div className="flex-1">
                                    <Field
                                        name="candidateEmail"
                                        type="email"
                                        className="h-10 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        placeholder="Enter Candidate email here"
                                        disabled={isViewMode || createLoading}
                                    />
                                    <ErrorMessage name="candidateEmail">
                                        {msg => <p className="text-xs text-red-600 mt-1">{msg}</p>}
                                    </ErrorMessage>
                                </div>
                            </div>

                            {/* Candidate Phone Number */}
                            <div className="flex items-center mb-4">
                                <label className="font-medium text-sm leading-[150%] tracking-normal text-gray-700 w-48 flex-shrink-0">
                                    Candidate Phone Number*
                                </label>
                                <div className="flex-1">
                                    <CommonPhoneInput
                                        value={values.candidatePhoneNumber}
                                        onChange={(phone, country) => handlePhoneNumberChange(phone, country, setFieldValue, setFieldError, setFieldTouched)}
                                        onBlur={() => setFieldTouched('candidatePhoneNumber', true)}
                                        placeholder="Enter phone number"
                                        disabled={isViewMode || createLoading}
                                        error={touched.candidatePhoneNumber && errors.candidatePhoneNumber}
                                    />
                                </div>
                            </div>

                            {/* Date of Interview */}
                            <div className="flex items-center mb-4">
                                <label className="font-medium text-sm leading-[150%] tracking-normal text-gray-700 w-48 flex-shrink-0">
                                    Date of Interview*
                                </label>
                                <div className="flex-1">
                                    <Field
                                        name="dateOfInterview"
                                        type="date"
                                        className="h-10 pl-3 pr-10 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        disabled={isViewMode || createLoading}
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                    <ErrorMessage name="dateOfInterview">
                                        {msg => <p className="text-xs text-red-600 mt-1">{msg}</p>}
                                    </ErrorMessage>
                                </div>
                            </div>

                            {/* Select Tools */}
                            <div className="flex items-center mb-4">
                                <label className="font-medium text-sm leading-[150%] tracking-normal text-gray-700 w-48 flex-shrink-0">
                                    Select Tools*
                                </label>
                                <div className="flex-1">
                                    <Select
                                        className="select-options"
                                        placeholder={isToolsLoading ? "Loading tools..." : "Select Tools"}
                                        options={toolOptions}
                                        value={values.selectTools}
                                        onChange={(selectedOptions) => {
                                            setFieldValue('selectTools', selectedOptions || []);
                                            setFieldTouched('selectTools', true, false);
                                        }}
                                        isDisabled={isViewMode || createLoading || isToolsLoading}
                                        isLoading={isToolsLoading}
                                        isMulti
                                    />
                                    <ErrorMessage name="selectTools">
                                        {msg => <p className="text-xs text-red-600 mt-1">{msg}</p>}
                                    </ErrorMessage>
                                </div>
                            </div>
                        </div>

                        {!isViewMode && (
                            <div className="flex dailog-footer gap-3 px-6 pb-6">
                                <button
                                    type="button"
                                    className="whiteBtnShadow btn border border-gray-100 w-full rounded-lg h-10 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-gray-300 focus:bg-white focus:border-gray-300 duration-100 secondary-btn disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={handleDialogClose}
                                    disabled={createLoading}
                                    style={{ backgroundColor: '#FFF4E5' }}
                                >
                                    Cancel
                                </button>

                                <BtnPrimary
                                    type="submit"
                                    disabled={createLoading || isSubmitting || isToolsLoading}
                                    className="w-full"
                                    style={{ backgroundColor: '#FF8E1C', borderColor: '#FF8E1C' }}
                                >
                                    {createLoading ? <ThreeDotLoader /> : isEdit ? "Update" : "Submit"}
                                </BtnPrimary>
                            </div>
                        )}

                        {isViewMode && (
                            <div className="flex dailog-footer px-6 pb-6">
                                <button
                                    type="button"
                                    className="whiteBtnShadow btn border border-gray-100 w-full rounded-lg h-10 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-gray-300 focus:bg-white focus:border-gray-300 duration-100 secondary-btn"
                                    onClick={handleDialogClose}
                                >
                                    Close
                                </button>
                            </div>
                        )}
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default AddCandidate;